/**
 * FollowUpChips · 方案预览卡下方「你可以继续问」追问 chips
 *
 * 数据：卡片可编辑且方案已 hydrate 后，调一次 /session/preview 主预览
 * （expand_path 空 + top_offset=0，follow_ups 仅此形态返回，接口文档 §四）
 * 拿服务端按优先级 / 适用规则筛好的至多 4 个 chip；下钻 / 翻页不再返回，
 * 本组件也不参与懒加载链路 —— 每张可编辑卡挂载时独立拉一次。
 *
 * 交互：点击 chip 把 text 原文作为下一条 query 发给 /send（sendUserMessage），
 * SSE streaming 中禁用防连点；「合并相似项」的固定回复由服务端 system prompt
 * 处理，前端无特殊分支。
 *
 * 生命周期：仅在「会话最后一条消息 且 editable」的方案卡下挂载（调用方
 * FolderPlanPreview 把关）—— 确认创建 / 被新方案取代转 readonly，或出现任何
 * 后续消息（含点击 chip 发出的 user 消息）不再是最后一条，本组件都随之卸载。
 * 拉取失败 / 空链 / 超量（422）按「无 chips」处理，整块不渲染。
 */

import React, { useEffect, useState } from 'react'
import { useAppSelector } from '../../../../app/hooks'
import { selectSessionStatus } from '../../../../slices/aiClassify'
import { useChatActions } from '../AIChat/ChatActionsContext'
import { fetchPreview } from '../../services/sessionApi'
import { sanitizeFollowUpChips } from './planPreviewUtils'
import type { FollowUpChip } from '../../../../types/ai-classify'
import styles from './FolderPlanPreview.module.scss'

/**
 * 拉取当前 session 的追问 chips（挂载时一次）
 *
 * sessionApi 不支持 AbortSignal，用 alive 标记防卸载后 setState；
 * 失败静默（渲染层按无 chips 处理，不打扰主流程）。
 */
function useFollowUpChips(groupID: string, sessionId: string): FollowUpChip[] {
  const [chips, setChips] = useState<FollowUpChip[]>([])

  useEffect(() => {
    if (!groupID || !sessionId) return undefined
    let alive = true
    fetchPreview(groupID, sessionId)
      .then((resp) => {
        if (!alive) return
        setChips(sanitizeFollowUpChips(resp?.follow_ups))
      })
      .catch(() => {
        // 静默：网络错 / 422（空链、expand 超上限）都按无 chips 处理
      })
    return () => {
      alive = false
    }
  }, [groupID, sessionId])

  return chips
}

export interface FollowUpChipsProps {
  /** 方案卡所属维度链 session · /session/preview 入参 */
  sessionId: string
}

const FollowUpChips: React.FC<FollowUpChipsProps> = ({ sessionId }) => {
  const groupID = useAppSelector((s) => s.group.groupId)
  const sessionStatus = useAppSelector(selectSessionStatus)
  const { sendUserMessage } = useChatActions()
  const chips = useFollowUpChips(String(groupID || ''), sessionId)

  if (chips.length === 0) return null

  return (
    <div className={styles.followUps}>
      <div className={styles.followUpsTitle}>你可以继续问</div>
      <div className={styles.followUpsChips}>
        {chips.map((chip) => (
          <button
            key={chip.key}
            type="button"
            className={styles.followUpChip}
            disabled={sessionStatus === 'streaming'}
            onClick={() => sendUserMessage(chip.text)}
          >
            {chip.text}
          </button>
        ))}
      </div>
    </div>
  )
}

export default FollowUpChips
